import { ComponentProps, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser, useSearch } from '@treams/treams-hooks';
import { AddIcon, CloseIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, IconColor, Paragraph, tokens } from '@treams/treams-ui';

import { RecipientUserOption } from '../../../kakapo-ui-services/models/Recipient';
import RecipientService from '../../../kakapo-ui-services/services/recipients/RecipientsService';
import RelativeContainer from '../atoms/RelativeContainer';
import Select from '../forms/controls/Select';
import RecipientSelectorOption from '../recipients/RecipientSelectorOption';

interface IButtonProps {
	small?: boolean;
}

const UserSelectorPlus = styled.div<IButtonProps>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	border: 1px dashed ${(props) => props.theme.colors.lightBlueDark};
	display: flex;
	align-items: center;
	justify-content: center;

	${({ small }) =>
		small &&
		`
    width: 28px;
		height: 28px;
		border-radius: 14px;
		padding: 7px;
  `}
`;

const UserSelectorButtonContainer = styled.button`
	width: 100%;
	padding: 0;
	background-color: transparent;

	:hover {
		${UserSelectorPlus} {
			border-color: ${tokens.text.secondary};
		}

		${Paragraph} {
			color: ${tokens.text.primary};
		}
	}
`;

export function UserSelectorButton(props: { onClick: () => void; small?: boolean }) {
	const { t } = useTranslation();

	return (
		<UserSelectorButtonContainer type="button" onClick={() => props.onClick()}>
			<Flex alignItems="center">
				<Box mr={3}>
					<UserSelectorPlus small={props.small}>
						<Box size={24}>
							<IconColor color="slateBlue">
								<AddIcon />
							</IconColor>
						</Box>
					</UserSelectorPlus>
				</Box>

				<Paragraph>{t('users.collaborators.selector.add.buttonTitle')}</Paragraph>
			</Flex>
		</UserSelectorButtonContainer>
	);
}

type Props = BoxProps & {
	selectedUsers: graphql.User[];
	onSelectUser: (user: graphql.User) => void;
	small?: boolean;
	excludeViewer?: boolean;
};

export const UserSelector: FC<Props> = ({
	selectedUsers,
	onSelectUser,
	small,
	excludeViewer,
	...boxProps
}) => {
	const { t } = useTranslation();

	const [isSelectorVisible, setIsSelectorVisible] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>('');
	const [selectedOption, setSelectedOption] = useState<any>(null);
	const [options, setOptions] = useState<RecipientUserOption[]>([]);

	const recipientsFromUser = (user: graphql.User): RecipientUserOption => {
		return {
			label: `${user.firstName} ${user.lastName}`,
			value: user.id!,
			data: user as any,
		};
	};

	const search = useSearch(inputValue, {
		excludeViewer: excludeViewer ?? true,
		status: [graphql.UserStatus.Active],
		skip: !inputValue,
	});

	const handleChange = (option: any) => {
		setIsSelectorVisible(false);
		setInputValue('');
		setSelectedOption(null);
		onSelectUser(option.data!);
	};

	useEffect(() => {
		setOptions(search.results.map((user) => recipientsFromUser(user as unknown as graphql.User)));
	}, [search.results]);

	return (
		<Box width={[1, 6 / 12]} mb={2} {...(boxProps as ComponentProps<BoxType>)}>
			{isSelectorVisible ? (
				<Select
					unspaced
					small
					autoFocus
					onInputChange={setInputValue}
					onBlur={() => {
						setIsSelectorVisible(false);
					}}
					inputValue={inputValue}
					noOptionsMessage={() =>
						inputValue.length > 0
							? t('users.collaborators.selector.noResults')
							: t('users.collaborators.selector.startTypingToSearch')
					}
					options={RecipientService.filterPreviouslyAddedOptions(
						options,
						selectedUsers.map((user) => ({
							userId: user.id!,
							userData: user as any,
						})),
					)}
					components={
						{
							Option: RecipientSelectorOption,
						} as any
					}
					onChange={handleChange}
					value={selectedOption}
				/>
			) : (
				<UserSelectorButton onClick={() => setIsSelectorVisible(true)} small={small} />
			)}
		</Box>
	);
};

const UserCollaboratorRemoveButton = styled.button`
	width: 20px;
	height: 20px;
	border-radius: 10px;
	position: absolute;
	top: -7px;
	right: -7px;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${tokens.text.primary};

	svg * {
		fill: ${(props) => props.theme.colors.white};
	}

	&:hover {
		background-color: ${(props) => props.theme.colors.white};

		svg * {
			fill: ${tokens.text.primary};
		}
	}
`;

const UserCollaboratorAvatarElement = styled(Flex)`
	${UserCollaboratorRemoveButton} {
		opacity: 0;
	}

	:hover {
		${UserCollaboratorRemoveButton} {
			opacity: 1;
		}
	}
`;

const UserCollaboratorAvatar = ({
	user,
	isOwner,
	isCurrentUserOwner,
	onUnshare,
}: {
	user: graphql.User;
	isOwner: boolean;
	isCurrentUserOwner: boolean;
	onUnshare: (user: graphql.User) => void;
}) => {
	const { t } = useTranslation();

	return (
		<UserCollaboratorAvatarElement width={[1, 6 / 12, 4 / 12]} mb={2} alignItems="center">
			<Box mr={3}>
				<RelativeContainer>
					<Avatar size={32} user={user} />
					{!isOwner && isCurrentUserOwner && (
						<UserCollaboratorRemoveButton onClick={() => onUnshare(user)}>
							<Box flexShrink={0} size={16}>
								<CloseIcon />
							</Box>
						</UserCollaboratorRemoveButton>
					)}
				</RelativeContainer>
			</Box>
			<Box>
				<Paragraph>
					{user.firstName} {user.lastName}
				</Paragraph>
				<Paragraph color="lightBlueDark">
					{isOwner
						? t('notes.collaborators.role.owner')
						: t('notes.collaborators.role.collaborator')}
				</Paragraph>
			</Box>
		</UserCollaboratorAvatarElement>
	);
};

export default function UserCollaboratorSelector({
	owner,
	collaborators,
	onShare,
	onUnshare,
	disabled,
}: {
	owner: graphql.User;
	collaborators: graphql.User[];
	onShare: (user: graphql.User) => void;
	onUnshare: (user: graphql.User) => void;
	disabled?: boolean;
}) {
	const authenticatedUser = useAuthenticatedUser();
	const isCurrentUserOwner = owner.id === authenticatedUser.id;

	return (
		<Flex flexWrap="wrap">
			{collaborators.map((user) => (
				<UserCollaboratorAvatar
					key={user.id}
					user={user}
					isOwner={user.id === owner.id}
					isCurrentUserOwner={isCurrentUserOwner}
					onUnshare={onUnshare}
				/>
			))}

			{!disabled && (
				<UserSelector
					selectedUsers={[owner, ...collaborators]}
					onSelectUser={(user) => onShare(user)}
				/>
			)}
		</Flex>
	);
}
